import clsx from "clsx"
import { useTranslation } from "react-i18next"
import { useAtom, useAtomValue } from "jotai"
import { Switch } from "./ui/Switch"
import {
	anchorPhotosAtom,
	currentPhotoAtom,
	displayGroupPhotosCountAtom,
	displayedPhotosAtom,
	isExpressSetupAtom,
} from "@/store/photo/photos-state"
import { toggleLock, toggleReference } from "@/store/style/reference"
import { isCalibrationAtom, projectAtom } from "@/store/project/projects-state"
import { FloatingTooltip, TooltipProps } from "./ui/FloatingTooltip"
import {
	editingDisabledAtom,
	markOnboardingShown,
	showingAdaptiveCropTooltipAtom,
	showingAdaptiveHealingTooltipAtom,
	showingAdaptiveMaskTooltipAtom,
	showingAdaptivePerspectiveTooltipAtom,
	showingAdvancedMarkReferenceTooltipAtom,
	showingExpressMarkReferenceTooltipAtom,
	showingImproveLockTooltipAtom,
	showingImproveMarkReferenceTooltipAtom,
	showingMarkReferenceReminderAtom,
} from "@/store/app-state"
import { Button } from "./ui/Button"
import { selectPhoto } from "@/store/photo/photos-selection-state"
import { useEffect, useMemo, useRef, useState } from "react"
import { adjustmentsHistoryAtom } from "@/store/store-history"
import { HistoryRecordAction, PhotoRecord } from "@/lib/catalog/types"
import { ShortcutKey } from "./Dialogs/ShortCutsDialog"
import { loadCurrentFile } from "@/store/editor/photo-loading"
import { Icon } from "./icons/Icon"
import { ContentTooltipWrapper } from "./ui/ContentTooltip"

export const StyleControls = () => {
	const { t } = useTranslation("components")
	const [{ references: referenceCount }] = useAtom(displayGroupPhotosCountAtom)
	const editingDisabled = useAtomValue(editingDisabledAtom)
	const [displayedPhotos] = useAtom(displayedPhotosAtom)
	const [suggestedPhotos] = useAtom(anchorPhotosAtom)
	const [currentPhoto] = useAtom(currentPhotoAtom)
	const project = useAtomValue(projectAtom)
	const adjustmentHistory = useAtomValue(adjustmentsHistoryAtom)
	const [isCalibration] = useAtom(isCalibrationAtom)
	const [isExpressSetup] = useAtom(isExpressSetupAtom)
	const [showAdvancedReferenceTooltip] = useAtom(showingAdvancedMarkReferenceTooltipAtom)
	const [showExpressReferenceTooltip] = useAtom(showingExpressMarkReferenceTooltipAtom)
	const [showImproveReferenceTooltip] = useAtom(showingImproveMarkReferenceTooltipAtom)
	const [showImproveLockTooltip] = useAtom(showingImproveLockTooltipAtom)
	const [showMarkReferenceReminderTooltip] = useAtom(showingMarkReferenceReminderAtom)
	const [showingAdaptiveMaskTooltip] = useAtom(showingAdaptiveMaskTooltipAtom)
	const [showingAdaptiveHealingTooltip] = useAtom(showingAdaptiveHealingTooltipAtom)
	const [showingAdaptiveCropTooltip] = useAtom(showingAdaptiveCropTooltipAtom)
	const [showingAdaptivePerspectiveTooltip] = useAtom(showingAdaptivePerspectiveTooltipAtom)

	const currentPhotoIndex = currentPhoto ? displayedPhotos.indexOf(currentPhoto) : 0
	const suggestedPhotoIndexes = suggestedPhotos.map((val) => displayedPhotos.indexOf(val))

	const [highlightMarkReference, setHighlightMarkReference] = useState(false)
	const [highlightMarkReferenceTimeout, setHighlightMarkReferenceTimeout] =
		useState<NodeJS.Timeout | null>(null)

	const clearHighlight = () => {
		highlightMarkReferenceTimeout && clearTimeout(highlightMarkReferenceTimeout)
		setHighlightMarkReferenceTimeout(null)
		setHighlightMarkReference(false)
	}
	useEffect(() => {
		const history = adjustmentHistory.slice(
			0,
			currentPhoto?.historyIndex != null ? currentPhoto?.historyIndex + 1 : undefined,
		)
		if (
			isCalibration &&
			currentPhoto &&
			currentPhoto.kind !== "reference" &&
			history.some((record) => {
				return (
					record.action !== HistoryRecordAction.Import &&
					record.action !== HistoryRecordAction.AutoEnhance
				)
			})
		) {
			highlightMarkReferenceTimeout && clearTimeout(highlightMarkReferenceTimeout)
			setHighlightMarkReferenceTimeout(
				setTimeout(() => {
					setHighlightMarkReference(true)
				}, 1000),
			)
		} else {
			clearHighlight()
		}

		return () => {
			clearHighlight()
		}
	}, [adjustmentHistory, currentPhoto?.historyIndex, currentPhoto?.kind])

	useEffect(() => {
		clearHighlight()
	}, [currentPhoto?.id])

	const isReference = () => {
		return currentPhoto?.kind == "reference"
	}

	const isLocked = () => {
		return currentPhoto?.kind == "locked" || currentPhoto?.kind == "reference"
	}

	const nextSuggestedPhoto = () => {
		const nextIndex = suggestedPhotoIndexes.find((val) => {
			return val > currentPhotoIndex
		})
		const nextPhoto = nextIndex ? displayedPhotos[nextIndex] : suggestedPhotos[0]
		if (nextPhoto) {
			selectPhoto(nextPhoto)
			void loadCurrentFile({
				kinds: ["preview", "full"],
			})
		}
	}

	const prevSuggestedPhoto = () => {
		const prevIndex = suggestedPhotoIndexes.findLast((val) => {
			return val < currentPhotoIndex
		})
		const prevPhoto = prevIndex
			? displayedPhotos[prevIndex]
			: suggestedPhotos[suggestedPhotos.length - 1]
		if (prevPhoto) {
			selectPhoto(prevPhoto)
			void loadCurrentFile({
				kinds: ["preview", "full"],
			})
		}
	}

	const getMarkReferenceTooltip = () => {
		if (showImproveLockTooltip) {
			return undefined
		}
		if (showAdvancedReferenceTooltip) {
			return {
				title: t("styleControls.markReferenceTitle"),
				main: t("styleControls.markReferenceMain"),
				onboardingKey: "calibrationTooltipsShown",
				index: 1,
				totalCount: 2,
			} as TooltipProps
		} else if (showExpressReferenceTooltip) {
			return {
				main: t("styleControls.markReferenceMain"),
				onboardingKey: "expressSetupTooltipShown",
				index: 2,
				totalCount: 2,
			} as TooltipProps
		} else if (showImproveReferenceTooltip) {
			return {
				title: t("styleControls.markReferenceEditTitle"),
				main: t("styleControls.markReferenceEditMain"),
				onboardingKey: "improveStyleTooltipsShown",
				index: 1,
				totalCount: 2,
			} as TooltipProps
		} else if (showMarkReferenceReminderTooltip) {
			return {
				title: t("styleControls.markFirstReferenceTitle"),
				main: t("styleControls.markFirstReferenceMain"),
				onboardingKey: "markReferenceReminderTooltipShown",
				index: 1,
				totalCount: 1,
			} as TooltipProps
		} else if (showingAdaptiveMaskTooltip) {
			return {
				title: t("styleControls.adaptiveMaskingTitle"),
				main: t("styleControls.adaptiveMaskingMain"),
				onboardingKey: "adaptiveMaskTooltipShown",
				index: 1,
				totalCount: 1,
			} as TooltipProps
		} else if (showingAdaptiveHealingTooltip) {
			return {
				title: t("styleControls.adaptiveHealingTitle"),
				main: t("styleControls.adaptiveHealingMain"),
				onboardingKey: "adaptiveHealingTooltipShown",
				index: 1,
				totalCount: 1,
			} as TooltipProps
		} else if (showingAdaptiveCropTooltip) {
			return {
				title: t("styleControls.adaptiveCropTitle"),
				main: t("styleControls.adaptiveCropMain"),
				onboardingKey: "adaptiveCropTooltipShown",
				index: 1,
				totalCount: 1,
			} as TooltipProps
		} else if (showingAdaptivePerspectiveTooltip) {
			return {
				title: t("styleControls.adaptivePerspectiveTitle"),
				main: t("styleControls.adaptivePerspectiveMain"),
				onboardingKey: "adaptivePerspectiveTooltipShown",
				index: 1,
				totalCount: 1,
			} as TooltipProps
		} else {
			return undefined
		}
	}

	const markReferenceToolTip = useMemo<TooltipProps | undefined>(
		() => getMarkReferenceTooltip(),
		[
			showImproveLockTooltip,
			showAdvancedReferenceTooltip,
			showExpressReferenceTooltip,
			showImproveReferenceTooltip,
			showMarkReferenceReminderTooltip,
			showingAdaptiveMaskTooltip,
			showingAdaptiveHealingTooltip,
			showingAdaptiveCropTooltip,
			showingAdaptivePerspectiveTooltip,
		],
	)

	const lockEditTooltip: TooltipProps = {
		title: t("styleControls.lockEditTooltipTitle"),
		main: t("styleControls.lockEditTooltipMain"),
		onboardingKey: "improveStyleTooltipsShown",
		index: 2,
		totalCount: 2,
	}

	return (
		<div
			aria-label={t("styleControls.ariaPhotoReferenceControls")}
			className="sticky top-0 z-10 flex w-full flex-col space-y-2 border-b border-base-900 bg-base-800 p-2"
		>
			{isCalibration && !isExpressSetup && (
				<div className="flex flex-col space-y-2 rounded-md bg-base-700 p-2">
					<div className="flex flex-row items-center justify-between text-sm font-bold">
						<div>{t("styleControls.setupProgress")}</div>
						<div>{`${referenceCount}/${suggestedPhotos.length}`}</div>
					</div>

					<div className="flex h-0.5 flex-row items-center justify-stretch space-x-0.5">
						{suggestedPhotos.map((val, index) => (
							<div
								key={val.fileName}
								className={clsx(
									index < referenceCount ? "bg-green-500" : "bg-base-400",
									"h-0.5 w-full",
								)}
							></div>
						))}
					</div>

					<div className="text-xs">
						{t("styleControls.bestResultsHint", { count: suggestedPhotos.length })}
					</div>

					<div className="flex w-full flex-none flex-row justify-between space-x-2">
						<Button
							trackingLabel="prev_suggestion"
							aria-label={t("styleControls.ariaPreviousAiSuggestion")}
							variant="primary"
							className="flex-1"
							size="sm"
							onClick={prevSuggestedPhoto}
						>
							{t("styleControls.prevSuggestion")}
						</Button>
						<Button
							trackingLabel="next_suggestion"
							aria-label={t("styleControls.ariaNextAiSuggestion")}
							variant="primary"
							className="flex-1"
							size="sm"
							onClick={nextSuggestedPhoto}
						>
							{t("styleControls.nextSuggestion")}
						</Button>
					</div>
				</div>
			)}

			<TooltipControl
				showTooltip={!!markReferenceToolTip}
				tooltip={markReferenceToolTip as TooltipProps}
			>
				<SwitchControl
					title={t("styleControls.markReferenceEdit")}
					shortcutKey="M"
					trackingLabel={isReference() ? "remove_reference" : "mark_reference"}
					trackingValues={{ projectId: project?.id }}
					description={t("styleControls.descMarkReference")}
					isChecked={isReference()}
					isDisabled={editingDisabled}
					isHighlighted={highlightMarkReference}
					onClick={() => {
						toggleReference(currentPhoto as PhotoRecord, true)
						if (markReferenceToolTip && markReferenceToolTip.onboardingKey) {
							markOnboardingShown(
								markReferenceToolTip.onboardingKey,
								markReferenceToolTip.index,
							)
						}
					}}
				/>
			</TooltipControl>

			{!isCalibration && (
				<TooltipControl showTooltip={showImproveLockTooltip} tooltip={lockEditTooltip}>
					<SwitchControl
						title={t("styleControls.lockEdit")}
						shortcutKey="L"
						trackingLabel={isLocked() ? "unlock_photo" : "lock_photo"}
						description={t("styleControls.descLockEdit")}
						isChecked={isLocked()}
						isDisabled={editingDisabled || isReference()}
						onClick={() => toggleLock(currentPhoto as PhotoRecord)}
					/>
				</TooltipControl>
			)}
		</div>
	)
}

const SwitchControl = (props: {
	title: string
	shortcutKey: string
	trackingLabel: string
	description: string
	trackingValue?: string
	trackingValues?: Record<string, any>
	isChecked?: boolean
	isDisabled?: boolean
	isHighlighted?: boolean
	onClick: () => void
}) => {
	const containerRef = useRef<HTMLDivElement>(null)

	return (
		<div ref={containerRef} className="flex w-full flex-none flex-col space-y-0.5 text-sm">
			<div className="flex flex-row items-center justify-between space-x-2 font-bold">
				<div aria-hidden className="flex flex-row items-center space-x-2">
					<div>{props.title}</div>
					<ContentTooltipWrapper
						tooltipDelay={0}
						tooltip={props.description}
						tooltipMaxWidth={280}
						tooltipProps={{ side: "bottom", sideOffset: 8 }}
					>
						<Icon name="info" color="gray" size={16} />
					</ContentTooltipWrapper>
				</div>
				<div className="flex flex-row items-center space-x-2">
					<ShortcutKey>{props.shortcutKey}</ShortcutKey>
					<Switch
						trackingLabel={props.trackingLabel}
						trackingValue={props.trackingValue}
						trackingValues={props.trackingValues}
						onCheckedChange={props.onClick}
						checked={props.isChecked}
						disabled={props.isDisabled}
						isHighlighted={props.isHighlighted}
					/>
				</div>
			</div>
		</div>
	)
}

const TooltipControl = (props: {
	showTooltip: boolean
	tooltip: TooltipProps
	children: React.ReactNode
}) => {
	return (
		<FloatingTooltip
			open={props.showTooltip}
			side="left"
			sideOffset={12}
			tooltip={props.tooltip}
		>
			<div
				className={clsx(
					props.showTooltip &&
						"rounded-sm outline outline-2 outline-offset-4 outline-accent-500",
				)}
			>
				{props.children}
			</div>
		</FloatingTooltip>
	)
}
